/**
 * Backtesting, second edition: the sessions as cards, each with its curve;
 * the open session's figures, whether the sample says anything yet, the
 * equity curve, its trades with a quick entry that keeps the keyboard going,
 * and how the results spread -- by size, and long against short.
 *
 * Results are read in R when the session says what one trade risks (1R): that
 * is what lets two strategies of different size be compared. Without it they
 * stay in the session's money. Trades are stored in money either way, so the
 * journal's own figures and the discipline gap read them unchanged.
 */

import { h } from '../vdom.js';
import { C, MONO, css, tap, input, select, monthPicker, datePicker, planLimit, planLine } from './common.js';
import { pill } from './cashflow.js';
import { DANGER_TEXT, DANGER_BG, DANGER_LINE, GAIN_BG, GAIN_LINE } from './whatif.js';
import { localDay, numberText, SUGGESTED_CURRENCIES, parseNumber } from '../../../shared/model.js';

const TIME_FRAMES = ['M1', 'M5', 'M15', 'H1', 'H4', 'D1', 'W', 'M', 'Y'];
/* The stored exits, in the order a quick entry offers them. */
const EXITS = ['TP hit', 'SL hit', 'BE', 'Manual'];
const EXIT_TONE = {
  'TP hit': [GAIN_BG, C.accentHi], 'SL hit': [DANGER_BG, DANGER_TEXT], BE: [C.line, C.textDim], Manual: ['rgba(227,194,122,.12)', C.gold]
};
const BUCKETS = [
  ['−1R', r => r <= -0.75], ['−0.5R', r => r > -0.75 && r < -0.1], ['BE', r => Math.abs(r) <= 0.1],
  ['+1R', r => r > 0.1 && r < 1.5], ['+2R', r => r >= 1.5 && r < 2.5], ['+3R+', r => r >= 2.5]
];
const BUCKET_COLORS = ['#8A3A40', '#5A2F33', C.ghost, '#2B6B57', '#34A585', C.accent];
const UI = {
  snOpen: false, snEdit: null, qOpen: false, qEdit: null, rev: 0, date: null,
  q: { side: 'LONG', tf: null, exit: null, unit: 'R', more: false }
};
const uiOf = state => state.backtestUi || UI;
const mono = (text, style = {}) => h('span', { style: css({ fontFamily: MONO, ...style }) }, text);
const tone = value => (value > 0.005 ? C.accent : value < -0.005 ? DANGER_TEXT : C.textDim);
const pct = ratio => (Math.max(0, Math.min(1, ratio)) * 100).toFixed(1) + '%';
const box = (mobile, style, ...children) => h('div', {
  style: css({
    padding: mobile ? '14px' : '16px 18px 18px', border: `1px solid ${C.stroke}`, borderRadius: mobile ? '16px' : '14px',
    background: C.card, display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, ...style
  })
}, ...children);
const title = (text, sub, mobile) => h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }) },
  h('div', { style: css({ fontSize: mobile ? '14px' : '15px', fontWeight: 700 }) }, text),
  sub && h('div', { key: 's', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.4 }) }, sub));

/**
 * A session's trades in its unit: R when 1R is set, money otherwise. In date
 * order (and entry order within a day), which is the order the curve runs in.
 */
export function sessionFigures(trades, session) {
  const risk = Number(session?.riskAmount) || 0;
  const unit = risk > 0 ? 'R' : 'money';
  const asc = trades.slice().sort((a, b) => String(a.date).localeCompare(String(b.date)) || String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
  const values = asc.map(row => (unit === 'R' ? (Number(row.netPl) || 0) / risk : Number(row.netPl) || 0));
  const small = unit === 'R' ? 0.1 : 0.005;
  let sum = 0;
  let peak = 0;
  let dd = 0;
  let streak = 0;
  let longest = 0;
  const curve = [0];
  values.forEach(value => {
    sum += value;
    curve.push(sum);
    peak = Math.max(peak, sum);
    dd = Math.max(dd, peak - sum);
    if (value < -small) { streak += 1; longest = Math.max(longest, streak); } else if (value > small) streak = 0;
  });
  const wins = values.filter(value => value > small);
  const losses = values.filter(value => value < -small);
  const won = wins.reduce((a, b) => a + b, 0);
  const lost = -losses.reduce((a, b) => a + b, 0);
  const n = values.length;
  return {
    unit, risk, asc, values, curve, sum, dd, longest, n,
    wins: wins.length, losses: losses.length, be: n - wins.length - losses.length,
    winRate: n ? wins.length / n : 0, ev: n ? sum / n : 0,
    pf: lost ? won / lost : (won ? Infinity : 0),
    avgWin: wins.length ? won / wins.length : 0, avgLoss: losses.length ? lost / losses.length : 0
  };
}

/** A result in the session's unit, with its sign. */
const unitText = (fmt, figures, value, currency, dp = 1) => (figures.unit === 'R'
  ? (value > 0.005 ? '+' : value < -0.005 ? '−' : '') + fmt.number(Math.abs(value), dp) + 'R'
  : fmt.signedAmount(value, currency));

/** A line through the running total, with the zero dashed across. */
function sparkline(curve, height, color, { big = false, empty = '' } = {}) {
  if (curve.length < 2) {
    return h('div', { style: css({ height: height + 'px', display: 'flex', alignItems: 'center', justifyContent: big ? 'center' : 'flex-start', fontSize: '12px', color: C.faint }) }, empty);
  }
  const width = 300;
  const low = Math.min(0, ...curve);
  const high = Math.max(0, ...curve);
  const span = high - low || 1;
  const pad = 4;
  const x = i => (i / (curve.length - 1)) * width;
  const y = v => pad + (1 - (v - low) / span) * (height - pad * 2);
  const points = curve.map((v, i) => x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');
  const zero = y(0).toFixed(1);
  return h('svg', { viewBox: `0 0 ${width} ${height}`, width: '100%', height: String(height), preserveAspectRatio: 'none', style: 'display:block;overflow:visible' },
    h('line', { x1: '0', x2: String(width), y1: zero, y2: zero, stroke: C.ghost, 'stroke-dasharray': '3 3', 'vector-effect': 'non-scaling-stroke' }),
    big && h('polygon', { key: 'fill', points: `0,${zero} ${points} ${width},${zero}`, fill: color, 'fill-opacity': '0.12' }),
    h('polyline', { points, fill: 'none', stroke: color, 'stroke-width': big ? '2' : '1.5', 'vector-effect': 'non-scaling-stroke', 'stroke-linejoin': 'round' }));
}

const period = (t, session) => {
  const from = session?.periodFrom;
  const to = session?.periodTo;
  if (!from) return '';
  const short = month => t.months[Number(month.slice(5, 7)) - 1] + ' ' + month.slice(2, 4);
  if (to && from.slice(0, 4) === to.slice(0, 4)) return t.months[Number(from.slice(5, 7)) - 1] + '–' + t.months[Number(to.slice(5, 7)) - 1] + ' ' + from.slice(0, 4);
  return short(from) + ' – ' + (to ? short(to) : t.shell.bt2Now);
};

// ------------------------------------------------------------ sessions ---

export function sessionStrip(ctx, { sessions, session, everything }) {
  const { t, fmt, state, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);

  const card = row => {
    const trades = everything.filter(trade => trade.backtestId === row.id);
    const f = sessionFigures(trades, row);
    const on = session && row.id === session.id;
    const frames = [...new Set(trades.map(trade => trade.timeFrame).filter(Boolean))].join('/');
    const meta = [row.instrument, frames, period(t, row)].filter(Boolean).join(' · ') || s.bt2NoSettings;
    return h('div', {
      key: row.id, ...tap(() => { if (!on) actions.selectBacktest(row.id); }, row.name), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        flex: mobile ? '0 0 82%' : '1 1 240px', maxWidth: '380px', minWidth: 0, padding: '14px 16px', borderRadius: '14px', cursor: 'pointer',
        border: `1px solid ${on ? C.accent : C.stroke}`, background: on ? C.line : C.card, display: 'flex', flexDirection: 'column', gap: '10px'
      })
    },
      h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '8px' }) },
        h('div', { style: css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' }) },
          h('span', { style: css({ fontSize: '14px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) }, row.name),
          mono(meta, { fontSize: '11px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' })),
        on && h('div', {
          key: 'edit', ...tap(() => openSettings(ctx, row), s.bt2Settings), class: 'pt-button', title: s.bt2Settings,
          style: css({ width: '32px', height: '32px', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: `1px solid ${C.stroke}`, color: C.muted, fontSize: '12px', cursor: 'pointer' })
        }, '✎')),
      h('div', { style: css({ height: '36px' }) }, sparkline(f.curve, 36, f.sum >= 0 ? C.accent : DANGER_TEXT, { empty: s.bt2NoTrades })),
      h('div', { style: css({ display: 'flex', justifyContent: 'space-between', gap: '8px', fontFamily: MONO, fontSize: '11.5px' }) },
        h('span', { style: css({ color: C.muted }) }, s.tlTrShort(f.n)),
        h('span', { style: css({ color: C.textDim }) }, f.n ? Math.round(f.winRate * 100) + '% WR' : '—'),
        h('span', { style: css({ fontWeight: 700, color: f.n ? tone(f.ev) : C.faint }) }, f.n ? unitText(fmt, f, f.ev, row.currency, 2) : '—'))
    );
  };

  return h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '10px' }) },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '6px 12px', flexWrap: 'wrap' }) },
      mono(s.bt2Sessions, { fontSize: '10.5px', letterSpacing: '1.2px', color: C.muted }),
      h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.bt2SessionsSub)),
    h('div', { role: 'radiogroup', style: css({ display: 'flex', flexWrap: mobile ? 'nowrap' : 'wrap', gap: '10px', overflowX: mobile ? 'auto' : 'visible', paddingBottom: '2px' }) },
      ...sessions.map(card),
      h('div', {
        key: 'new', ...tap(() => openSettings(ctx, null), s.bt2New), class: 'pt-button',
        style: css({
          flex: mobile ? '0 0 82%' : '1 1 240px', maxWidth: '380px', minHeight: '132px', padding: '12px', border: `1px dashed ${C.strokeHi}`, borderRadius: '14px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', textAlign: 'center', cursor: 'pointer'
        })
      },
        mono(s.bt2New, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.bt2NewHint))),
    sessions.length === 0 && !ui.snOpen && h('div', { key: 'none', style: css({ fontSize: '12.5px', color: C.muted, lineHeight: 1.5 }) }, s.btNone),
    ui.snOpen && sessionForm(ctx, { sessions, everything })
  );
}

function openSettings({ state, actions }, session) {
  actions.setBacktestUi({ snOpen: true, snEdit: session ? session.id : null, rev: uiOf(state).rev + 1, from: session?.periodFrom || null, to: session?.periodTo || null });
  actions.askDeleteBacktest(false);
}

/** A new session, or the open one's settings -- and, only from there, deleting it after asking. */
function sessionForm(ctx, { sessions, everything }) {
  const { t, fmt, state, inputs, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);
  const editing = ui.snEdit ? sessions.find(row => row.id === ui.snEdit) : null;
  const close = () => { actions.setBacktestUi({ snOpen: false, snEdit: null, rev: ui.rev + 1 }); actions.askDeleteBacktest(false); };
  const read = name => parseNumber(inputs.value(name));
  const currency = editing?.currency || 'USD';
  /* The free plan's sessions: the ones there are stay; only another new one is refused. */
  const limit = editing ? null : planLimit(state, 'backtestSessions');
  const spent = limit !== null && sessions.length >= limit;

  const save = async () => {
    if (spent) return actions.showError('api:plan_backtests');
    const name = inputs.value('snName').trim();
    if (!name) return actions.showError(s.btNameNeeded);
    const capital = read('snCap');
    const risk = read('snRisk');
    if ((capital !== null && capital < 0) || (risk !== null && risk <= 0)) return actions.showError(s.bt2NumbersNeeded);
    if (ui.from && ui.to && ui.from > ui.to) return actions.showError(s.bt2PeriodOrder);
    const payload = {
      name, instrument: inputs.value('snInst').trim().toUpperCase(), periodFrom: ui.from || null, periodTo: ui.to || null,
      capital, riskAmount: risk, currency: inputs.value('snCur') || currency
    };
    const row = editing ? await actions.updateBacktest(editing.id, payload) : await actions.addBacktest(payload);
    if (row) close();
  };

  const label = text => h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) }, text);
  const frame = { height: '44px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel };
  const field = (name, text, value, { placeholder = '', number = false, upper = false, wide = false, unit = null } = {}) => h('label', {
    key: name, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0, ...(wide ? { gridColumn: '1 / -1' } : {}) })
  },
    label(text),
    h('div', { style: css({ ...frame, display: 'flex', alignItems: 'center', gap: '6px', padding: '0 12px' }) },
      input(inputs, name, placeholder, {
        type: number ? 'number' : 'text', value, onEnter: save,
        style: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', padding: 0, fontFamily: number || upper ? MONO : 'inherit', fontSize: number || upper ? '14px' : '13.5px', fontWeight: upper ? 700 : 400, textTransform: upper ? 'uppercase' : 'none' }
      }),
      unit && mono(unit, { key: 'u', fontSize: '11.5px', color: C.faint })));
  const monthField = (key, text, id) => h('div', { key, style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
    label(text),
    monthPicker({
      id, value: ui[key] || '', onPick: stamp => actions.setBacktestUi({ [key]: stamp }), label: text, title: text, note: s.bt2PeriodNote, mobile,
      state, actions, t, fmt,
      triggerStyle: { ...frame, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '13px', color: ui[key] ? C.text : C.faint, letterSpacing: '.6px' }
    }));
  const count = editing ? everything.filter(row => row.backtestId === editing.id).length : 0;
  const money = value => (value === null || value === undefined ? undefined : numberText(value));

  return h('div', {
    key: 'snform-' + (editing ? editing.id : 'new') + '-' + ui.rev, class: 'pt-rise',
    style: css({ padding: '14px', borderRadius: '12px', background: C.panel, border: `1px solid ${editing ? C.goldDeep : C.stroke}`, display: 'flex', flexDirection: 'column', gap: '12px' })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      mono(editing ? s.bt2FormEdit : s.bt2FormNew, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.muted }),
      h('div', { style: css({ flex: 1 }) }),
      h('div', { ...tap(close, s.cfClose), class: 'pt-button', title: s.cfClose, style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer' }) }, '×')),
    h('div', { style: css({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${mobile ? 130 : 150}px, 1fr))`, gap: '10px' }) },
      field('snName', s.bt2Name, editing ? editing.name : undefined, { placeholder: s.bt2NamePh, wide: true }),
      field('snInst', s.tlPairL, editing ? editing.instrument : undefined, { placeholder: s.jPairHint, upper: true }),
      monthField('from', s.bt2From, 'bt-from'),
      monthField('to', s.bt2To, 'bt-to'),
      field('snCap', s.bt2Capital, editing ? money(editing.capital) : undefined, { placeholder: '2500', number: true }),
      field('snRisk', s.bt2Risk, editing ? money(editing.riskAmount) : undefined, { placeholder: '25', number: true }),
      h('label', { key: 'cur', style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.ivCurF),
        select(inputs, 'snCur', SUGGESTED_CURRENCIES.includes(currency) ? SUGGESTED_CURRENCIES.map(code => ({ value: code, label: code })) : [currency, ...SUGGESTED_CURRENCIES].map(code => ({ value: code, label: code })),
          { height: '44px', borderRadius: '10px', fontFamily: MONO, fontSize: '13px' }, { value: currency }))),
    limit !== null && planLine(spent ? s.planBacktestsSpent : s.planBacktestsUsed(sessions.length, limit), { spent, key: 'plan' }),
    editing && state.backtestConfirm
      ? h('div', { key: 'confirm', style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', padding: '10px 12px', borderRadius: '10px', border: `1px solid ${DANGER_LINE}`, background: DANGER_BG }) },
        h('span', { style: css({ flex: '1 1 220px', fontSize: '12.5px', color: DANGER_TEXT, lineHeight: 1.45 }) }, s.btDeleteAsk(count)),
        h('div', { ...tap(() => actions.askDeleteBacktest(false), s.cfCancel), style: css({ height: '40px', display: 'flex', alignItems: 'center', padding: '0 12px', color: C.muted, fontSize: '13px', cursor: 'pointer' }) }, s.cfCancel),
        h('div', {
          ...tap(() => { actions.removeBacktest(editing.id); close(); }, s.btDeleteYes), class: 'pt-button',
          style: css({ height: '40px', display: 'flex', alignItems: 'center', padding: '0 16px', borderRadius: '9px', background: C.danger, color: C.bg, fontFamily: MONO, fontSize: '12px', fontWeight: 700, cursor: 'pointer' })
        }, s.btDeleteYes))
      : h('div', { key: 'foot', style: css({ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }) },
        editing && h('div', {
          key: 'del', ...tap(() => actions.askDeleteBacktest(true), s.bt2Delete), class: 'pt-button',
          style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 12px', borderRadius: '9px', color: DANGER_TEXT, fontSize: '13px', cursor: 'pointer' })
        }, s.bt2Delete),
        /* Trades are kept in money: with some logged, a new 1R changes what each one reads in R. */
        h('span', { style: css({ flex: '1 1 220px', fontSize: '12px', color: C.muted, lineHeight: 1.5 }) },
          editing && everything.some(row => row.backtestId === editing.id) ? s.bt2RiskNoteKept : s.bt2RiskNote),
        h('div', { ...tap(close, s.cfCancel), style: css({ height: '44px', display: 'flex', alignItems: 'center', padding: '0 14px', color: C.muted, fontSize: '13px', cursor: 'pointer' }) }, s.cfCancel),
        pill(editing ? s.cfSaveChanges : s.bt2Create, save, { primary: true, key: 'save' }))
  );
}

// ------------------------------------------------------------ figures ---

export function sessionKpis(trades, session, { t, fmt, mobile }) {
  const s = t.shell;
  const f = sessionFigures(trades, session);
  const cur = session.currency || 'USD';
  const money = value => (f.unit === 'R' ? fmt.signedAmount(value * f.risk, cur) : fmt.signedAmount(value, cur));
  const u = (value, dp) => unitText(fmt, f, value, cur, dp);
  const tile = (label, value, sub, { color = C.text, tinted = false } = {}) => h('div', {
    style: css({
      padding: mobile ? '12px 13px' : '16px 18px', borderRadius: '14px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: mobile ? '5px' : '7px',
      border: `1px solid ${tinted ? GAIN_LINE : C.stroke}`, background: tinted ? GAIN_BG : C.card
    })
  },
    mono(label, { fontSize: mobile ? '10px' : '10.5px', letterSpacing: '1.2px', color: C.muted }),
    mono(value, { fontSize: mobile ? '18px' : '24px', fontWeight: 700, letterSpacing: mobile ? 0 : '-0.6px', color, whiteSpace: 'nowrap' }),
    h('div', { style: css({ fontSize: mobile ? '11.5px' : '12.5px', color: C.textDim, lineHeight: 1.4 }) }, sub));
  const n = f.n;
  return h('div', { style: css({ display: 'grid', gap: '12px', gridTemplateColumns: mobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(200px, 1fr))' }) },
    tile(s.bt2Ev, n ? u(f.ev, 2) : '—', n ? (f.unit === 'R' ? s.bt2EvSub(money(f.ev)) : s.btExpectancySub) : s.btExpectancySub,
      { color: n ? tone(f.ev) : C.faint, tinted: n > 0 && f.ev > 0.005 }),
    tile(s.jWinRate, n ? Math.round(f.winRate * 100) + '%' : '—', s.bt2WinSub(f.wins, f.losses, f.be)),
    tile(s.btProfitFactor.toUpperCase(), !n ? '—' : f.pf === Infinity ? '∞' : fmt.number(f.pf, 2),
      n ? s.bt2PfSub(u(f.avgWin), u(-f.avgLoss)) : s.btProfitFactorSub,
      { color: !n ? C.faint : f.pf >= 1.5 ? C.accent : f.pf >= 1 ? C.text : DANGER_TEXT }),
    tile(s.btDrawdown.toUpperCase(), n ? u(-f.dd) : '—', n ? (f.unit === 'R' ? money(-f.dd) + ' · ' : '') + s.btDrawdownSub(f.longest) : s.bt2DdSub,
      { color: n && f.dd > 0 ? DANGER_TEXT : C.faint })
  );
}

/** Thirty trades before a result starts to mean anything; a hundred before the drawdown does too. */
export function sampleCard(trades, session, { t, fmt }) {
  const s = t.shell;
  const f = sessionFigures(trades, session);
  const n = f.n;
  const state = n < 30 ? [s.bt2SampleShort(n), s.bt2SampleShortSub(30 - n), C.gold, n / 30]
    : n < 100 ? [s.bt2SampleOk(n), s.bt2SampleOkSub, C.accent, n / 100]
      : [s.bt2SampleStrong(n), s.bt2SampleStrongSub, C.accent, 1];
  const cur = session.currency || 'USD';
  const capital = Number(session.capital) || 0;
  const gained = f.unit === 'R' ? f.sum * f.risk : f.sum;
  const right = capital
    ? s.bt2Capital2(fmt.amount(Math.round(capital), cur), fmt.amount(Math.round(capital + gained), cur), (gained >= 0 ? '+' : '−') + fmt.percent(Math.abs(gained / capital), 1))
    : s.bt2SetCapital;
  return h('div', {
    style: css({ padding: '12px 16px', border: `1px solid ${C.stroke}`, borderRadius: '14px', background: C.card, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px 20px' })
  },
    h('div', { style: css({ flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
      h('span', { style: css({ fontSize: '14px', fontWeight: 700, color: state[2] }) }, state[0]),
      h('span', { style: css({ fontSize: '12px', color: C.textDim, lineHeight: 1.4 }) }, state[1])),
    h('div', { style: css({ flex: '1 1 200px', minWidth: '160px', height: '6px', borderRadius: '3px', background: C.stroke, overflow: 'hidden' }) },
      h('div', { style: css({ width: pct(state[3]), height: '100%', borderRadius: '3px', background: state[2] }) })),
    mono(right, { fontSize: '12px', color: C.textDim, whiteSpace: 'nowrap' })
  );
}

// ---------------------------------------------------- curve and trades ---

export function curveCard(trades, session, { t, fmt, mobile }) {
  const s = t.shell;
  const f = sessionFigures(trades, session);
  const cur = session.currency || 'USD';
  const p = period(t, session);
  return box(mobile, {},
    h('div', { style: css({ display: 'flex', alignItems: 'flex-start', gap: '10px' }) },
      title(s.btEquity, (f.unit === 'R' ? s.bt2CurveR : s.btEquitySub) + (p ? ' · ' + p : ''), mobile),
      h('div', { style: css({ flex: 1 }) }),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
        mono(f.n ? unitText(fmt, f, f.sum, cur) : (f.unit === 'R' ? '0R' : fmt.amount(0, cur)), { fontSize: '16px', fontWeight: 700, color: tone(f.sum), whiteSpace: 'nowrap' }),
        f.unit === 'R' && f.n > 0 && mono(fmt.signedAmount(f.sum * f.risk, cur), { key: 'm', fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }))),
    h('div', { style: css({ height: mobile ? '140px' : '180px' }) },
      sparkline(f.curve, mobile ? 140 : 180, f.sum >= 0 ? C.accent : DANGER_TEXT, { big: true, empty: s.bt2CurveEmpty }))
  );
}

export function sessionTrades(trades, session, ctx) {
  const { t, fmt, state, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);
  const f = sessionFigures(trades, session);
  const cur = session.currency || 'USD';
  const rows = f.asc.map((row, i) => ({ row, i, value: f.values[i] })).reverse();

  const line = ({ row, i, value }) => {
    const [y, mo, d] = String(row.date).split('-');
    const exit = EXIT_TONE[row.exitLogic] || EXIT_TONE.BE;
    return h('div', {
      key: row.id,
      style: css({
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', minHeight: '52px', padding: '8px 6px',
        borderBottom: `1px solid ${C.line}`, background: ui.qEdit === row.id ? C.line : 'transparent'
      })
    },
      !mobile && mono('#' + (i + 1), { key: 'i', flex: '0 0 30px', fontSize: '11px', color: C.faint }),
      mono(Number(d) + ' ' + t.months[Number(mo) - 1] + ' ' + String(y).slice(2), { flex: '0 0 82px', fontSize: '12px' }),
      mono(row.side, { flex: '0 0 auto', padding: '2px 7px', borderRadius: '5px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.5px', background: row.side === 'SHORT' ? DANGER_BG : GAIN_BG, color: row.side === 'SHORT' ? DANGER_TEXT : C.accentHi }),
      row.timeFrame && mono(row.timeFrame, { key: 'tf', flex: '0 0 auto', padding: '2px 6px', borderRadius: '5px', border: `1px solid ${C.stroke}`, fontSize: '10.5px', color: C.textDim }),
      h('span', { style: css({ flex: '1 1 110px', minWidth: 0, fontSize: '12.5px', color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) },
        [row.setup, row.note].filter(Boolean).join(' · ') || '—'),
      row.exitLogic && h('span', {
        key: 'exit', style: css({ flex: '0 0 auto', padding: '2px 8px', borderRadius: '5px', background: exit[0], fontSize: '11px', fontWeight: 600, color: exit[1] })
      }, s.tlExits[row.exitLogic] || row.exitLogic),
      h('div', { style: css({ flex: '0 0 76px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '1px', marginLeft: mobile ? 'auto' : 0 }) },
        mono(unitText(fmt, f, value, row.currency || cur), { fontSize: '14px', fontWeight: 700, color: tone(value), whiteSpace: 'nowrap' }),
        f.unit === 'R' && mono(fmt.signedAmount(Number(row.netPl) || 0, row.currency || cur), { key: 'm', fontSize: '11px', color: C.muted, whiteSpace: 'nowrap' })),
      h('div', { style: css({ flex: '0 0 auto', display: 'flex', gap: '2px' }) },
        h('div', {
          ...tap(() => editTrade(ctx, row, f), s.editTradeTitle), class: 'pt-button', title: s.editTradeTitle,
          style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', border: `1px solid ${C.stroke}`, color: C.muted, fontSize: '12px', cursor: 'pointer' })
        }, '✎'),
        h('div', {
          ...tap(() => actions.removeTrade(row.id), s.removeLabel), class: 'pt-remove', title: s.removeLabel,
          style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.faint, fontSize: '16px', cursor: 'pointer' })
        }, '×'))
    );
  };

  return box(mobile, { gap: '6px' },
    h('div', { style: css({ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap', paddingBottom: '6px' }) },
      h('div', { style: css({ fontSize: mobile ? '14px' : '15px', fontWeight: 700 }) }, s.tlListBacktest),
      f.n > 0 && h('div', { key: 'sub', style: css({ fontSize: '12.5px', color: C.muted }) }, s.bt2ListSub(f.n))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}`, maxHeight: '520px', overflowY: 'auto' }) },
      ...rows.map(line),
      rows.length === 0 && h('div', { key: 'empty', style: css({ padding: '22px 6px', fontSize: '13px', color: C.muted, textAlign: 'center', lineHeight: 1.5 }) }, s.btEmpty)),
    ui.qOpen
      ? quickEntry(trades, session, ctx, f)
      : h('div', {
        key: 'open', ...tap(() => openQuick(ctx, session, f), s.tlNew), class: 'pt-button',
        style: css({
          marginTop: '8px', minHeight: '60px', padding: '12px 16px', border: `1px dashed ${C.strokeHi}`, borderRadius: '12px', cursor: 'pointer',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', textAlign: 'center'
        })
      },
        mono(s.tlNew, { fontSize: '12.5px', fontWeight: 700, letterSpacing: '1.4px', color: C.accent }),
        h('span', { style: css({ fontSize: '12.5px', color: C.muted }) }, s.bt2QuickHint))
  );
}

/* The next trade starts on the date and time frame of the last one: a replay walks forward through the chart. */
function openQuick({ state, actions }, session, f) {
  const ui = uiOf(state);
  const last = f.asc[f.asc.length - 1];
  actions.setBacktestUi({
    qOpen: true, qEdit: null, rev: ui.rev + 1,
    date: last ? last.date : (session.periodFrom ? session.periodFrom + '-01' : localDay()),
    q: { ...ui.q, exit: null, tf: last?.timeFrame || ui.q.tf, unit: f.unit === 'R' ? ui.q.unit : '$', more: false }
  });
  focusResult();
}

function editTrade({ state, actions }, row, f) {
  actions.setBacktestUi({
    qOpen: true, qEdit: row.id, rev: uiOf(state).rev + 1, date: row.date,
    q: { side: row.side || 'LONG', tf: row.timeFrame || null, exit: row.exitLogic || null, unit: f.unit === 'R' ? 'R' : '$', more: Boolean(row.setup || row.note) }
  });
  focusResult();
}

function focusResult() {
  if (typeof requestAnimationFrame === 'function' && typeof document !== 'undefined') {
    requestAnimationFrame(() => {
      const node = document.getElementById('bt-result');
      if (!node) return;
      node.closest('#bt-quick')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      node.focus({ preventScroll: true });
    });
  }
}

/**
 * Direction and result, and Enter saves and clears for the next one. The result
 * goes in as R (times the session's 1R) or as money; without a chosen exit, a
 * win counts as TP, a full loss as SL and nothing as BE.
 */
function quickEntry(trades, session, ctx, f) {
  const { t, fmt, state, inputs, actions, mobile } = ctx;
  const s = t.shell;
  const ui = uiOf(state);
  const q = ui.q || UI.q;
  const editing = ui.qEdit ? trades.find(row => row.id === ui.qEdit) : null;
  const cur = session.currency || 'USD';
  const canR = f.unit === 'R';
  const unit = canR ? q.unit : '$';
  const setQ = patch => actions.setBacktestUi({ q: { ...q, ...patch } });
  const close = () => actions.setBacktestUi({ qOpen: false, qEdit: null, rev: ui.rev + 1 });

  const save = () => {
    const typed = parseNumber(inputs.value('btR'));
    if (typed === null) return actions.showError(s.tlResultNeeded);
    const r = unit === 'R' ? typed : (canR ? typed / f.risk : null);
    const netPl = Math.round((unit === 'R' ? typed * f.risk : typed) * 100) / 100;
    const auto = r !== null
      ? (r > 0.1 ? 'TP hit' : r <= -0.9 ? 'SL hit' : Math.abs(r) <= 0.1 ? 'BE' : 'Manual')
      : (netPl > 0 ? 'TP hit' : netPl < 0 ? 'SL hit' : 'BE');
    const trade = {
      date: ui.date || localDay(),
      pair: session.instrument || (editing ? editing.pair : '') || s.bt2PairFallback,
      side: q.side || 'LONG',
      netPl,
      currency: cur,
      timeFrame: q.tf || '',
      exitLogic: q.exit || auto,
      setup: inputs.value('btSetup').trim(),
      note: inputs.value('btNote').trim(),
      /* A replay is the rules by definition; the honesty questions belong to the live journal. */
      rulesFollowed: true,
      moneyManagement: true
    };
    if (editing) {
      actions.saveTradeEdit(editing.id, trade);
      close();
      return;
    }
    actions.addTrade({ ...trade, lots: 0, backtestId: session.id });
    /* Ready for the next one: the result and the note clear, the setup stays. */
    const result = inputs.node('btR');
    if (result) { result.value = ''; result.focus({ preventScroll: true }); }
    const note = inputs.node('btNote');
    if (note) note.value = '';
    setQ({ exit: null });
  };

  const label = text => h('span', { style: css({ fontSize: '11.5px', color: C.muted, whiteSpace: 'nowrap' }) }, text);
  const frame = { height: '44px', border: `1px solid ${C.strokeHi}`, borderRadius: '10px', background: C.panel };
  const seg = (key, options, { clear = false, colors = {}, grid = false } = {}) => h('div', {
    role: 'radiogroup',
    style: css({
      ...(grid ? { display: 'grid', gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` } : { display: 'flex', overflowX: 'auto' }),
      gap: '3px', padding: '3px', borderRadius: '10px', border: `1px solid ${C.strokeHi}`, background: C.panel, minHeight: '44px', boxSizing: 'border-box'
    })
  }, ...options.map(([value, text]) => {
    const on = q[key] === value;
    return h('div', {
      key: String(value), ...tap(() => setQ({ [key]: on && clear ? null : value }), text), role: 'radio', 'aria-checked': on ? 'true' : 'false',
      style: css({
        flex: grid ? 'none' : '1 0 44px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', borderRadius: '7px', cursor: 'pointer', whiteSpace: 'nowrap',
        background: on ? (colors[String(value)] || C.strokeHi) : 'transparent', color: on ? C.text : C.muted, fontFamily: MONO, fontSize: '12px', fontWeight: 700
      })
    }, text);
  }));
  const quick = [[-1, '−1R', 'SL hit'], [0, 'BE', 'BE'], [1, '+1R', 'TP hit'], [2, '+2R', 'TP hit'], [3, '+3R', 'TP hit']];

  return h('div', {
    key: 'quick-' + (editing ? editing.id : 'new') + '-' + ui.rev, id: 'bt-quick', class: 'pt-rise',
    style: css({
      marginTop: '8px', padding: '14px', borderRadius: '12px', background: C.panel, display: 'flex', flexDirection: 'column', gap: '12px',
      border: `1px solid ${editing ? C.goldDeep : GAIN_LINE}`
    })
  },
    h('div', { style: css({ display: 'flex', alignItems: 'center', gap: '10px' }) },
      mono(editing ? s.tlFormEdit : s.bt2Quick, { fontSize: '10.5px', letterSpacing: '1.2px', color: editing ? C.gold : C.accent }),
      h('span', { style: css({ flex: 1, minWidth: 0, fontSize: '11.5px', color: C.faint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }) },
        [session.instrument, canR ? s.bt2OneR(fmt.amount(f.risk, cur)) : s.bt2NoR].filter(Boolean).join(' · ')),
      h('div', { ...tap(close, s.cfClose), class: 'pt-button', title: s.cfClose, style: css({ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', color: C.muted, fontSize: '16px', cursor: 'pointer' }) }, '×')),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }) },
      h('div', { style: css({ flex: '1 1 140px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.bt2Candle),
        datePicker({
          id: 'bt-date', value: ui.date || '', onPick: stamp => stamp && actions.setBacktestUi({ date: stamp }), label: s.bt2Candle, state, actions, t, fmt, mobile,
          triggerStyle: { ...frame, display: 'flex', alignItems: 'center', padding: '0 12px', fontSize: '13px', color: C.text, letterSpacing: '.6px' }
        })),
      h('div', { style: css({ flex: '1 1 150px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.tlSideL),
        seg('side', [['LONG', 'LONG'], ['SHORT', 'SHORT']], { grid: true, colors: { LONG: '#1F4A3D', SHORT: '#5A2F33' } })),
      h('div', { style: css({ flex: '1 1 170px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.bt2Result),
        h('div', { style: css({ ...frame, border: `1px solid ${C.accent}`, display: 'flex', alignItems: 'center', gap: '6px', padding: '0 4px 0 12px' }) },
          h('input', {
            id: 'bt-result', ref: inputs.ref('btR'), type: 'text', inputmode: 'decimal', autocomplete: 'off',
            placeholder: unit === 'R' ? '+2 / −1' : '+50 / −25', 'aria-label': s.bt2Result,
            value: editing ? numberText(unit === 'R' ? Math.round((Number(editing.netPl) || 0) / f.risk * 100) / 100 : editing.netPl) : undefined,
            onkeydown: event => { if (event.key === 'Enter') { event.preventDefault(); save(); } },
            style: css({ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', color: C.text, fontFamily: MONO, fontSize: '15px', fontWeight: 700 })
          }),
          canR && seg('unit', [['R', 'R'], ['$', cur === 'USD' ? '$' : cur]]))),
      h('div', { style: css({ flex: '2 1 240px', display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.tlExitL),
        seg('exit', EXITS.map(exit => [exit, s.bt2Exits[exit] || exit]), { clear: true, grid: true }))),
    h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '5px' }) },
      label(s.tlTfL),
      seg('tf', TIME_FRAMES.map(tf => [tf, tf]), { clear: true })),
    h('div', { style: css({ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }) },
      h('span', { style: css({ fontSize: '11.5px', color: C.faint, marginRight: '2px' }) }, s.bt2Fast),
      ...quick.map(([r, text, exit]) => h('div', {
        key: text, ...tap(() => {
          const node = inputs.node('btR');
          if (node) { node.value = numberText(unit === 'R' ? r : r * f.risk); node.focus({ preventScroll: true }); }
          setQ({ exit });
        }, text), class: 'pt-button',
        style: css({
          height: '36px', minWidth: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 10px', borderRadius: '8px', cursor: 'pointer',
          border: `1px solid ${r < 0 ? DANGER_LINE : r > 0 ? GAIN_LINE : C.strokeHi}`, fontFamily: MONO, fontSize: '12px', fontWeight: 700, color: r < 0 ? DANGER_TEXT : r > 0 ? C.accentHi : C.textDim
        })
      }, text)).filter(() => canR),
      h('div', { style: css({ flex: 1 }) }),
      h('div', {
        ...tap(() => setQ({ more: !q.more }), q.more ? s.bt2Less : s.bt2More), class: 'pt-button',
        style: css({ height: '36px', display: 'flex', alignItems: 'center', padding: '0 10px', borderRadius: '8px', fontSize: '12.5px', color: C.textDim, cursor: 'pointer' })
      }, q.more ? s.bt2Less : s.bt2More)),
    /* Kept in the DOM while folded, so a setup typed once stays for the next entries. */
    h('div', { style: css({ display: q.more ? 'grid' : 'none', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: '10px' }) },
      h('label', { style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.tlSetupL),
        input(inputs, 'btSetup', s.bt2SetupPh, { value: editing ? editing.setup : undefined, onEnter: save, style: { ...frame, padding: '0 12px', fontSize: '13.5px' } })),
      h('label', { style: css({ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: 0 }) },
        label(s.bt2Note),
        input(inputs, 'btNote', s.bt2NotePh, { value: editing ? editing.note : undefined, onEnter: save, style: { ...frame, padding: '0 12px', fontSize: '13.5px' } }))),
    h('div', { style: css({ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px' }) },
      h('span', { style: css({ flex: '1 1 200px', fontSize: '12px', color: C.muted, lineHeight: 1.5 }) }, canR ? s.bt2AutoExit : s.bt2SetRisk),
      pill(editing ? s.cfSaveChanges : s.bt2Save, save, { primary: true, key: 'save' }))
  );
}

// ------------------------------------------------------- distributions ---

export function spreadCards(trades, session, { t, fmt, mobile }) {
  const s = t.shell;
  const f = sessionFigures(trades, session);
  const cur = session.currency || 'USD';
  const sides = ['LONG', 'SHORT'].map(side => ({ side, figures: sessionFigures(trades.filter(row => row.side === side), session) }));

  const spread = f.unit === 'R' && (() => {
    const counts = BUCKETS.map(([, test]) => f.values.filter(test).length);
    const max = Math.max(1, ...counts);
    return box(mobile, {},
      title(s.bt2Spread, s.bt2SpreadSub, mobile),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: '6px', alignItems: 'end', height: '130px', borderBottom: `1px solid ${C.ghost}` }) },
        ...counts.map((count, i) => h('div', { key: 'b' + i, style: css({ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: '5px', minWidth: 0 }) },
          mono(String(count), { fontSize: '11.5px', fontWeight: 700, color: count ? C.textDim : C.faint }),
          h('div', { style: css({ width: '100%', maxWidth: '44px', height: (count / max * 78).toFixed(1) + '%', minHeight: '2px', borderRadius: '4px 4px 2px 2px', background: BUCKET_COLORS[i] }) })))),
      h('div', { style: css({ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0,1fr))', gap: '6px', marginTop: '-6px' }) },
        /* The half in the reader's own decimal mark: 0.5 in English, 0,5 in Polish. */
        ...BUCKETS.map(([text], i) => mono(text.replace('0.5', fmt.number(0.5, 1)), { key: 'l' + i, textAlign: 'center', fontSize: '10.5px', color: C.muted, whiteSpace: 'nowrap' }))));
  })();

  return [
    spread || null,
    box(mobile, { gap: '10px' },
      title(s.bt2Sides, null, mobile),
      h('div', { style: css({ display: 'flex', flexDirection: 'column', borderTop: `1px solid ${C.ghost}` }) },
        ...sides.map(({ side, figures }) => h('div', {
          key: side, style: css({ display: 'grid', gridTemplateColumns: '62px minmax(0,1fr) auto', gap: '4px 12px', alignItems: 'center', minHeight: '58px', padding: '6px 0', borderBottom: `1px solid ${C.line}` })
        },
          mono(side, { justifySelf: 'start', padding: '2px 7px', borderRadius: '5px', fontSize: '10.5px', fontWeight: 700, letterSpacing: '.5px', background: side === 'SHORT' ? DANGER_BG : GAIN_BG, color: side === 'SHORT' ? DANGER_TEXT : C.accentHi }),
          h('div', { style: css({ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }) },
            mono(s.tlTrShort(figures.n) + ' · ' + (figures.n ? Math.round(figures.winRate * 100) + '%' : '—') + ' WR', { fontSize: '12px', color: C.textDim }),
            h('span', { style: css({ fontSize: '11.5px', color: C.muted }) }, s.bt2Total(figures.n ? unitText(fmt, figures, figures.sum, cur) : '—'))),
          h('div', { style: css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }) },
            mono(figures.n ? unitText(fmt, figures, figures.ev, cur, 2) : '—', { fontSize: '14px', fontWeight: 700, color: figures.n ? tone(figures.ev) : C.faint }),
            h('span', { style: css({ fontSize: '11px', color: C.muted }) }, s.btExpectancy))))))
  ];
}
